import { inject, Injectable, Injector, OnDestroy, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { filter } from 'rxjs';
import { interval, Subscription } from 'rxjs';
import { ROUTE_LOGIN } from '../../shared/constants/routes';
import { AuthService } from '../../shared/services/auth.service';
import { DialogService } from '../../shared/services/dialog.service';
import { SessionStore } from '../../shared/stores/session.store';
import {
  SESSION_TIMER_DURATION_MS,
  SESSION_TICK_MS,
  SESSION_WARNING_THRESHOLD_MS,
} from '../constants/session';

/**
 * Runs a client-side countdown when the user is authenticated. When remaining time
 * is at or below the warning threshold, opens the Extend Session dialog. On extend,
 * resets the timer and refreshes the session; on logout, AuthService handles cleanup.
 */
@Injectable({ providedIn: 'root' })
export class SessionExpiryService implements OnDestroy {
  private readonly sessionStore = inject(SessionStore);
  private readonly dialogService = inject(DialogService);
  private readonly authService = inject(AuthService);
  private readonly injector = inject(Injector);
  private readonly router = inject(Router);

  /** Remaining time in milliseconds. 0 when no session or timer not running. */
  readonly remainingMs = signal(0);

  private tickSubscription: Subscription | null = null;
  private extendDialogOpen = false;

  /** When the current countdown started (Date.now()). Used to recalculate on visibility change. */
  private sessionTimerStartedAt = 0;

  constructor() {
    const session$ = toObservable(this.sessionStore.currentSession, { injector: this.injector });

    // Start timer when authenticated
    session$.pipe(filter((s) => s != null && s !== undefined)).subscribe(() => this.startTimer());

    // Stop timer when logged out or loading
    session$.pipe(filter((s) => s == null || s === undefined)).subscribe(() => this.stopTimer());
  }

  /** Stop timer and remove visibility listener on destroy */
  ngOnDestroy(): void {
    this.stopTimer();
  }

  /** Helper method to start a session timeout */
  private startTimer(): void {
    // Stop existing timer
    this.stopTimer();
    this.sessionTimerStartedAt = Date.now();
    this.remainingMs.set(SESSION_TIMER_DURATION_MS);

    this.tickSubscription = interval(SESSION_TICK_MS).subscribe(() => {
      const current = this.sessionStore.currentSession();
      if (current == undefined) {
        return;
      }

      const elapsed = Date.now() - this.sessionTimerStartedAt;
      const next = Math.max(0, SESSION_TIMER_DURATION_MS - elapsed);
      this.remainingMs.set(next);

      if (next <= 0) {
        this.doLogout();
        return;
      }

      this.checkOpenDialog(next);
    });
  }

  /** Reset the countdown to full duration. Call when user extends session. */
  resetTimer(): void {
    this.sessionTimerStartedAt = Date.now();
    this.remainingMs.set(SESSION_TIMER_DURATION_MS);
  }

  /** Helper method to stop current timer */
  private stopTimer(): void {
    if (this.tickSubscription) {
      this.tickSubscription.unsubscribe();
      this.tickSubscription = null;
    }
    this.remainingMs.set(0);
  }

  /** Helper method to check and open dialog if needed */
  private checkOpenDialog(next: number): void {
    if (next > SESSION_WARNING_THRESHOLD_MS || this.extendDialogOpen) return;

    this.extendDialogOpen = true;

    this.dialogService.openExtendSession().subscribe((result) => {
      if (result === 'extend') {
        this.authService.refreshSession().subscribe();
        this.resetTimer();
      } else if (result === 'logout') {
        this.doLogout();
      }

      setTimeout(() => (this.extendDialogOpen = false), 1000);
    });
  }

  /** Helper to logout */
  private doLogout(): void {
    this.authService.logout().subscribe(() => this.router.navigate([ROUTE_LOGIN]));
  }
}
